import {Component, Input, OnInit} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent} from "@components";
import {Country, Lookups, UserDetail} from "@model";
import {FormControl, FormGroup} from "@angular/forms";
import {LocalizedDatePipe, RolePipe} from "@pipes";
import {take} from "rxjs";

@Component({
  selector: 'app-user-details-data',
  templateUrl: './user-details-data.component.html',
  styleUrls: ['./user-details-data.component.scss']
})
export class UserDetailsDataComponent extends AbstractComponent implements OnInit {

  @Input() userDetails: UserDetail | undefined;
  @Input() competitor: boolean = false;
  @Input() basicDataTitle: string = "";
  @Input() raceDataTitle: string = "";
  @Input() isBasicDataCollapsed: boolean = false;
  @Input() isRaceDataCollapsed: boolean = true;
  @Input() genders: Lookups[] = [];
  @Input() meals: Lookups[] = [];
  @Input() transports: Lookups[] = [];
  @Input() countries: Country[] = [];
  @Input('sizes') tshirtSizes: Lookups[] = [];

  inputForm: FormGroup = new FormGroup({
    firstname: new FormControl(),
    lastname: new FormControl(),
    gender: new FormControl(),
    birthDay: new FormControl(),
    userId: new FormControl(),
    role: new FormControl(),
    created: new FormControl(),
    modified: new FormControl(),
    email: new FormControl(),
    phoneNumber: new FormControl(),
    language: new FormControl(''),
    sports: new FormControl(),
    club: new FormControl(),
    clubs: new FormControl(),
    category: new FormControl(),
    height: new FormControl(),
    weight: new FormControl(),
    tShirtSize: new FormControl(),
    footwearSize: new FormControl(),
    identityCardNumber: new FormControl(),
    identityCardIssuer: new FormControl(),
    identityCardExpires: new FormControl(),
    emergencyPhone: new FormControl(),
    meal: new FormControl(),
    transport: new FormControl(),
  });
  localizedDatePipe: LocalizedDatePipe;

  constructor(public translocoService: TranslocoService, private rolePipe: RolePipe) {
    super();
    this.localizedDatePipe = new LocalizedDatePipe(translocoService);
  }

  ngOnInit(): void {
    this.populateForm();
  }

  getCountryName(): string {
    let country: Country | undefined = this.countries.find((country: Country) => {
      return country.id === this.userDetails?.country;
    });
    return country?.name ?? "-"
  }

  populateForm(): void {
    this.patchFormValue('birthDay', this.userDetails?.birthDay, 'shortDate');
    this.patchFormValue('created', this.userDetails?.created, 'shortDate');
    this.patchFormValue('modified', this.userDetails?.modified, 'shortDate');
    this.patchFormValue('identityCardExpires', this.userDetails?.identityCardExpires, 'shortDate');

    const simpleMappings = {
      firstname: this.userDetails?.firstname ?? "-",
      lastname: this.userDetails?.lastname ?? "-",
      userId: this.userDetails?.userId ?? "-",
      role: this.rolePipe.transform(this.userDetails?.role),
      gender: '-',
      email: this.userDetails?.email ?? "-",
      phoneNumber: this.userDetails?.phoneNumber ?? "-",
      sports: this.userDetails?.sports ?? "-",
      club: this.userDetails?.club ?? "-",
      clubs: this.userDetails?.clubs ?? "-",
      category: this.userDetails?.category ?? "-",
      height: this.userDetails?.height ?? "-",
      weight: this.userDetails?.weight ?? "-",
      tShirtSize: this.userDetails?.tshirtSize ?? "-",
      footwearSize: this.userDetails?.footwearSize ?? "-",
      identityCardNumber: this.userDetails?.identityCardNumber ?? "-",
      identityCardIssuer: this.userDetails?.identityCardIssuer ?? "-",
      emergencyPhone: this.userDetails?.emergencyPhone ?? "-",
      meal: this.userDetails?.meal ?? "-",
      transport: this.getTransportName() ?? "-",
    };
    this.inputForm.patchValue(simpleMappings);

    // Asynchronous handling for gender due to translation requirement
    if (this.userDetails?.gender) {
      this.translocoService.selectTranslate('app.common.gender.' + this.userDetails.gender)
      .pipe(take(1)) // Ensures subscription completes after first emission
      .subscribe(translatedGender => {
        this.inputForm.patchValue({gender: translatedGender});
      });
    }

    const languageKey = this.userDetails?.language
      ? `app.common.language.${this.userDetails.language}`
      : "-";

    // Use TranslocoService to translate the language code
    this.translocoService.selectTranslate(languageKey)
    .subscribe(translatedLanguage => {
      // Use the translated language name as the form control value
      this.inputForm.patchValue({
        language: translatedLanguage || '-'
      });
    });
  }

  private patchFormValue(fieldName: string, value: any, dateFormat?: string): void {
    if (!value) {
      this.inputForm.patchValue({[fieldName]: "-"});
      return;
    }
    const transformedValue = dateFormat
      ? this.localizedDatePipe.transform(value, dateFormat)
      : value;
    this.inputForm.patchValue({[fieldName]: transformedValue});
  }

  getTransportName(): string {
    let transport: Lookups | undefined = this.transports.find((value: Lookups) => {
      return value.id === this.userDetails?.transport;
    });
    return transport?.name ?? "-"
  }

  getTShirtSizeName(): string {
    let tshirtSize: Lookups | undefined = this.tshirtSizes.find((value: Lookups) => {
      return value.id === this.userDetails?.tshirtSize;
    });
    return tshirtSize?.name ?? "-"
  }

  getMealName(): string {
    let meal: Lookups | undefined = this.meals.find((value: Lookups) => {
      return value.id === this.userDetails?.meal;
    });
    return meal?.name ?? "-"
  }
}

